import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import {
  useMunicipality,
  useMunicipalityAvailableEditions,
  useMunicipalitySnapshot,
  useCounty,
  useCountyMakrolage,
  useCountySnapshot,
  useStateData,
  useStateSnapshot,
  useTextIndicators,
} from '../hooks/queries'
import EditionSelector from '../components/EditionSelector'
import DataSourceSection from '../components/DataSourceSection'
import IndicatorSection from '../components/IndicatorSection'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { OverviewTab, KreisTab, BBSRTab, LagedatenKennzahlenTab, LagepromptSection } from './municipality-detail'

type TabId = 'overview' | 'lagedaten' | 'gemeinde' | 'kreis' | 'bundesland' | 'bund' | 'bbsr'

export default function MunicipalityDetailPage() {
  const { id: municipalityKey } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [selectedEdition, setSelectedEdition] = useState<number | null>(null)

  // ==========================================================================
  // React Query Hooks - all data fetching is now declarative
  // ==========================================================================

  // Municipality data
  const { data: municipality, isLoading: municipalityLoading, error: municipalityError } = useMunicipality(municipalityKey)
  const { data: availableEditions = [], isLoading: editionsLoading } = useMunicipalityAvailableEditions(municipalityKey)

  // When "Aktuell" is selected (null), use the latest available edition (first in array, sorted desc)
  const effectiveEdition = selectedEdition ?? (availableEditions.length > 0 ? availableEditions[0] : null)
  const { data: currentSnapshot } = useMunicipalitySnapshot(municipalityKey, effectiveEdition)

  // County data (enabled when municipality is loaded)
  const countyId = municipality?.county?.id
  const countyKey = municipality?.county?.county_key
  const { data: countyData } = useCounty(countyKey)
  const { data: makrolageData } = useCountyMakrolage(countyId)
  const { data: currentCountySnapshot } = useCountySnapshot(countyKey, effectiveEdition)

  // State data (enabled when municipality is loaded)
  const stateId = municipality?.state?.id
  const stateCode = municipality?.state?.code
  const { data: stateData } = useStateData(stateId)
  const { data: currentStateSnapshot } = useStateSnapshot(stateCode, effectiveEdition)

  // Text Indicators lookup for displaying Textkennzeichen names
  const { getDisplayText: getTextkennzeichenName } = useTextIndicators()

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Übersicht' },
    { id: 'lagedaten', label: 'Lagedaten & Kennzahlen' },
    { id: 'gemeinde', label: 'Gemeinde' },
    { id: 'kreis', label: 'Kreis' },
    { id: 'bundesland', label: 'Bundesland' },
    { id: 'bund', label: 'Bund' },
    { id: 'bbsr', label: 'BBSR-Regionen' },
  ] as const

  // Check if current tab should show the EditionSelector sidebar
  const showYearSidebar = ['gemeinde', 'kreis', 'bundesland', 'bund'].includes(activeTab)

  // ==========================================================================
  // Loading & Error States
  // ==========================================================================

  if (municipalityLoading) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-muted">Lade Daten...</p>
        </div>
      </div>
    )
  }

  if (municipalityError || !municipality) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <div className="card-gut8 max-w-md">
          <h2 className="text-xl font-semibold text-secondary mb-4">Fehler</h2>
          <p className="text-text-muted mb-4">
            {municipalityError instanceof Error ? municipalityError.message : 'Gemeinde nicht gefunden'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Zurück zur Suche
          </button>
        </div>
      </div>
    )
  }

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="page-container min-h-screen flex flex-col">
      {/* Dark Header Section */}
      <div className="bg-gut8-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Header />

          {/* Back button & Title */}
          <div className="pb-6">
            <button
              onClick={() => navigate('/')}
              className="text-primary hover:text-secondary mb-4 flex items-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Zurück zur Suche
            </button>
            <h2 className="text-3xl font-bold text-text-inverse">{municipality.name}</h2>
            <p className="mt-2 text-text-inverse/70">
              {municipality.county?.name}, {municipality.state?.name}
            </p>
          </div>

          {/* Tabs */}
          <div className="border-b border-text-inverse/20">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-inverse/70 hover:text-text-inverse hover:border-text-inverse/40'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Content with optional sidebar */}
          <div className={showYearSidebar ? 'flex gap-6' : ''}>
            {/* Sidebar - only for Verwaltungshierarchie tabs */}
            {showYearSidebar && availableEditions.length > 0 && (
              <aside className="w-56 flex-shrink-0">
                <div className="sticky top-8">
                  <EditionSelector
                    availableEditions={availableEditions}
                    selectedEdition={selectedEdition}
                    onChange={setSelectedEdition}
                    latestEdition={availableEditions[0]}
                    loading={editionsLoading}
                  />
                </div>
              </aside>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {activeTab === 'overview' && municipalityKey && (
                <>
                  <OverviewTab
                    municipality={municipality}
                    municipalityKey={municipalityKey}
                    currentSnapshot={currentSnapshot}
                    getTextkennzeichenName={getTextkennzeichenName}
                    onNavigateTab={setActiveTab}
                  />
                  <LagepromptSection municipalityKey={municipalityKey} />
                </>
              )}

              {activeTab === 'lagedaten' && municipalityKey && (
                <LagedatenKennzahlenTab municipalityKey={municipalityKey} />
              )}

              {activeTab === 'gemeinde' && municipality && (
                <div className="space-y-6">
                  <DataSourceSection
                    title={`Gemeindeverzeichnis: ${municipality.name}`}
                    dataSource={currentSnapshot?.data_source ?? undefined}
                    data={{
                      // GV100AD - Allgemeine Daten
                      gemeindeschlüssel: municipality.municipality_key,
                      ars: municipality.ars,
                      namenszusatz: municipality.name_addon,
                      ...(municipality.designation && municipality.designation !== municipality.name
                        ? { amtl_bezeichnung: municipality.designation }
                        : {}),
                      // GV100NAD - Namens- und Anschriftendaten
                      textkennzeichen: municipality.textkennzeichen_label || municipality.textkennzeichen,
                      siedlungstyp: currentSnapshot?.settlement_type || municipality.settlement_type,
                      gemeindetyp: municipality.municipality_type,
                      gemeindeverband: municipality.municipal_association?.name,
                    }}
                  />
                  <IndicatorSection
                    entityType="municipality"
                    ags={municipality.ags}
                    maxYear={effectiveEdition ? effectiveEdition - 1 : undefined}
                  />
                </div>
              )}

              {activeTab === 'kreis' && municipality && (
                <KreisTab
                  municipality={municipality}
                  makrolageData={makrolageData}
                  currentCountySnapshot={currentCountySnapshot}
                  effectiveEdition={effectiveEdition}
                />
              )}

              {activeTab === 'bundesland' && municipality && (stateData || currentStateSnapshot) && (
                <div className="space-y-6">
                  <DataSourceSection
                    title={`Gemeindeverzeichnis: ${municipality.state?.name || 'N/A'}`}
                    dataSource={currentStateSnapshot?.data_source ?? undefined}
                    data={{
                      // GV100AD - Allgemeine Daten
                      bundeslandcode: currentStateSnapshot ? municipality.state?.code : stateData?.state_code,
                      name: currentStateSnapshot ? currentStateSnapshot.name : stateData?.state_name,
                      bezeichnung: stateData?.state_designation,
                      landeshauptstadt: stateData?.state_capital,
                      anzahl_kreise: currentStateSnapshot?.num_counties ?? stateData?.num_counties,
                      einwohner: currentStateSnapshot ? currentStateSnapshot.population : stateData?.population,
                      fläche_km2: currentStateSnapshot ? currentStateSnapshot.area_km2 : stateData?.area_km2,
                      bevölkerungsdichte: currentStateSnapshot ? currentStateSnapshot.population_density : stateData?.population_density,
                    }}
                  />
                  {municipality.state?.code && (
                    <IndicatorSection
                      entityType="state"
                      ags={municipality.state.code.toString().padStart(2, '0')}
                      maxYear={effectiveEdition ? effectiveEdition - 1 : undefined}
                    />
                  )}
                </div>
              )}

              {activeTab === 'bundesland' && municipality && !stateData && (
                <div className="status-error text-center">
                  <p>Keine Bundesland-Daten verfügbar</p>
                </div>
              )}

              {activeTab === 'bund' && (
                <div className="space-y-6">
                  {/* Indicators (Regionalstatistik, INKAR, calculated, etc.) */}
                  <IndicatorSection
                    entityType="federal"
                    ags="DG"
                    maxYear={effectiveEdition ? effectiveEdition - 1 : undefined}
                  />
                </div>
              )}

              {activeTab === 'bbsr' && (
                <BBSRTab
                  municipality={municipality}
                  countyData={countyData}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gut8-bg-primary mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </div>
  )
}
